/**
 * DxfBomButton — single-click BOM (Bill-of-Materials) Excel export.
 *
 * BE: ``GET /dxf-library/files/{id}/export/bom`` streams an xlsx with one
 * sheet per block-type plus a "Riepilogo" summary. The endpoint returns
 * 422 when the drawing has no block components yet — we surface that as a
 * non-blocking inline message instead of throwing.
 */

import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { FileDown, Loader2 } from "lucide-react";
import { exportDxfBom, type DxfFile } from "@ando/core";

export interface DxfBomButtonProps {
  readonly file: DxfFile;
  readonly variant?: "primary" | "ghost";
  readonly compact?: boolean;
}

type DownloadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "done" }
  | { kind: "empty" }
  | { kind: "error"; message: string };

export function DxfBomButton({
  file,
  variant = "ghost",
  compact = false,
}: DxfBomButtonProps) {
  const { t } = useLingui();
  const [state, setState] = useState<DownloadState>({ kind: "idle" });

  async function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    setState({ kind: "loading" });
    try {
      const { blob, filename } = await exportDxfBom(file.content_item_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setState({ kind: "done" });
      window.setTimeout(() => setState({ kind: "idle" }), 2500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // BE returns 422 with a "no blocks" body when the file has no
      // extractable block components — degrade gracefully.
      if (msg.toLowerCase().includes("422") || msg.toLowerCase().includes("block")) {
        setState({ kind: "empty" });
      } else {
        setState({ kind: "error", message: msg });
      }
      window.setTimeout(() => setState({ kind: "idle" }), 4000);
    }
  }

  const label =
    state.kind === "loading"
      ? t`Generazione…`
      : state.kind === "done"
      ? t`Scaricato`
      : state.kind === "empty"
      ? t`Nessun blocco`
      : state.kind === "error"
      ? t`Errore`
      : compact
      ? "BOM"
      : t`Esporta BOM`;

  const base =
    "inline-flex items-center gap-1.5 rounded-md text-[11px] font-medium transition-colors disabled:opacity-60";
  const sizing = compact ? "px-2 py-1" : "px-2.5 py-1.5";
  const palette =
    variant === "primary"
      ? "bg-ando-orange text-white hover:bg-ando-orange/90"
      : "bg-white/65 text-ink-2 hover:bg-white";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={state.kind === "loading"}
      className={`${base} ${sizing} ${palette}`}
      aria-label={t`Esporta distinta base per ${file.filename}`}
      title={
        state.kind === "empty"
          ? t`Questo disegno non contiene blocchi estraibili.`
          : state.kind === "error"
          ? state.message
          : t`Esporta la distinta base in Excel`
      }
    >
      {state.kind === "loading" ? (
        <Loader2 size={12} className="animate-spin" />
      ) : (
        <FileDown size={12} strokeWidth={1.75} />
      )}
      <span>{label}</span>
    </button>
  );
}
DxfBomButton.displayName = "DxfBomButton";
